import DatePicker from '@material-ui/lab/DatePicker';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack5';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Form, FormikProvider, useFormik } from 'formik';
// material
import { LoadingButton } from '@material-ui/lab';
import { Autocomplete, Box, Button, Card, Checkbox, Grid, Stack, TextField } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import SaveIcon from '@material-ui/icons/Save';
import * as moment from 'moment';
import { getCustomerList } from '../../../redux/slices/customer';
import { useDispatch } from '../../../redux/store';
import axios from '../../../utils/axios';
// routes
import { PATH_DASHBOARD } from '../../../routes/paths';
import Label from '../../Label';
//
import ReceiptTransferList from './ReceiptTransferList';
import CustomList from './CustomList';
import AutocompleteInput from '../../autocomplete/autocomplete-input';

// ----------------------------------------------------------------------

InvoiceNewForm.propTypes = {
  isEdit: PropTypes.bool,
  serie: PropTypes.string,
  currentInvoice: PropTypes.object
};

export default function InvoiceNewForm({ isEdit, serie, currentInvoice }) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { customerList } = useSelector((state) => state.customer);
  const [receipts, setReceipts] = useState([]);
  const { companiesList, selectedCompanyId } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const [customerPendingReceipts, setCustomerPendingReceipts] = useState([]);
  const [customerId, setCustomerId] = useState(1);
  const [newDate] = useState(new Date());
  const isRective = serie === 'R';
  const backPath = isRective ? PATH_DASHBOARD.amend.series : PATH_DASHBOARD.invoice.series;
  const [showIBAN, setShowIBAN] = useState(false);
  const [exempt, setExempt] = useState(false);
  const [invoicesForRective, setInvoicesForRective] = useState([]);
  const [selectedForRective, setSelectedForRective] = useState([]);
  const [customerDefault, setCustomerDefault] = useState(serie === 'C' ? 22 : 1);
  const [selectedIndex, setSelectedIndex] = useState(selectedCompanyId);

  // eslint-disable-next-line no-unused-vars
  const inputRefs = useRef([]);

  const InvoiceSchema = Yup.object().shape({
    customerId: Yup.number().required('El cliente es requerido'),
    date: Yup.date().required('La fecha es requerida'),
    showIban: Yup.bool(),
    exempt: Yup.bool(),
    receipts: Yup.array().required('Es necesario añadir lineas para crear un albarán'),
    rectiveInvoices: Yup.array().when('isRective', {
      is: true,
      then: Yup.array().required('Tienes que seleccionar al menos una factura para realizar el abono')
    })
  });

  const handleSelectedReceipts = (receipts) => {
    setReceipts(receipts);
  };

  const invoiceFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      customerId: currentInvoice?.customer.id || customerDefault,
      date: currentInvoice?.date ? new Date(currentInvoice.date) : newDate,
      showIban: currentInvoice?.showIBAN,
      exempt: currentInvoice?.exempt,
      receipts: currentInvoice?.receipts || [],
      rectiveInvoices: currentInvoice?.rectiveInvoices || []
    },
    validationSchema: InvoiceSchema,
    onSubmit: async (values, { setSubmitting, resetForm, setErrors }) => {
      let rectiveData = {};
      if (isRective) {
        rectiveData = {
          rectiveInvoices: selectedForRective
        };
      }
      const invoice = {
        customerId: values.customerId,
        date: moment(values.date),
        number: currentInvoice?.number || '',
        showIBAN,
        exempt,
        receipts,
        ...rectiveData
      };
      try {
        if (isEdit) {
          await axios.put(`/api/v1/${selectedCompanyId}/invoice/series/${serie}/${currentInvoice.id}`, invoice);
        } else {
          await axios.post(`/api/v1/${selectedCompanyId}/invoice/series/${serie}`, invoice);
        }
        resetForm();
        setSubmitting(false);
        enqueueSnackbar(!isEdit ? 'Factura creada con éxito' : 'Factura actualizada con éxito', { variant: 'success' });
      } catch (error) {
        const errorMessage = error.status ? error.status : error;
        enqueueSnackbar(errorMessage, { variant: 'error' });
        setSubmitting(false);
        setErrors(error);
      } finally {
        const routePath = isRective ? PATH_DASHBOARD.amend.series : PATH_DASHBOARD.invoice.series;
        navigate(`${routePath}${serie}`);
      }
    }
  });

  const getCustomerReceipts = useCallback(() => {
    try {
      return axios.get(`/api/v1/${selectedCompanyId}/customer/${customerId}/receipts`);
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Ha habido un error', { variant: 'error' });
    }
    return [];
  }, [customerId, enqueueSnackbar, selectedCompanyId]);

  const getCustomerForRectiveInvoices = useCallback(() => {
    try {
      return axios.get(`/api/v1/${selectedIndex}/customer/${customerId}/invoiceRectives`);
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Ha habido un error', { variant: 'error' });
    }
    return [];
  }, [customerId, enqueueSnackbar, selectedIndex]);

  useEffect(() => {
    if (customerList.length === 0) {
      dispatch(getCustomerList());
    } else {
      setCustomerDefault(customerList[0].id);
    }
  }, [customerList, dispatch]);

  useEffect(() => {
    if (currentInvoice) {
      setReceipts(currentInvoice.receipts);
      setSelectedForRective(currentInvoice.rectiveInvoices);
      setCustomerId(currentInvoice.customer.id);
      setShowIBAN(currentInvoice.showIBAN);
      setExempt(currentInvoice.exempt);
    }
  }, [currentInvoice, selectedIndex]);

  useEffect(() => {
    getCustomerReceipts().then((response) => {
      setCustomerPendingReceipts(response.data);
    });
  }, [getCustomerReceipts]);

  useEffect(() => {
    if (isRective) {
      getCustomerForRectiveInvoices().then((response) => {
        setInvoicesForRective(response.data);
      });
    }
  }, [getCustomerForRectiveInvoices, isRective]);

  const { errors, values, touched, handleSubmit, isSubmitting, setFieldValue, getFieldProps } = invoiceFormik;

  const handleToggle = (selectedInvoice) => {
    if (isEdit) return;

    const selecteds = [...selectedForRective];

    if (selectedInvoice) {
      if (selecteds.includes(selectedInvoice.id)) {
        const index = selecteds.indexOf(selectedInvoice.id);
        if (index > -1) {
          selecteds.splice(index, 1);
        }
        setSelectedForRective(selecteds);
      } else {
        selecteds.push(selectedInvoice.id);
        setSelectedForRective(selecteds);
      }
    }
  };

  const handleFilterCompany = (event, newValue) => {
    setSelectedIndex(newValue.value);
  };

  return (
    <FormikProvider value={invoiceFormik}>
      <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ p: 3 }}>
              <Stack spacing={3}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 3, sm: 2 }}>
                  <Autocomplete
                    disableClearable
                    fullWidth
                    clearOnBlur
                    options={customerList.map((option) => option)}
                    getOptionLabel={(option) => option.name}
                    value={customerList.filter((customer) => customer.id === values.customerId)[0] || 1}
                    noOptionsText="Sin opciones"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Seleccionar cliente..."
                        InputProps={{ ...params.InputProps, type: 'search' }}
                        error={Boolean(touched.customerId && errors.customerId)}
                        helperText={touched.customerId && errors.customerId}
                      />
                    )}
                    onChange={(event, newValue) => {
                      setFieldValue('customerId', newValue.id);
                      setCustomerId(newValue.id);
                    }}
                  />
                  <DatePicker
                    label="Fecha"
                    disabled
                    {...getFieldProps('date')}
                    onChange={(date) => setFieldValue('date', date)}
                    renderInput={(params) => (
                      <TextField fullWidth {...params} error={Boolean(touched.date && errors.date)} />
                    )}
                    inputFormat="dd/MM/yyyy"
                  />
                </Stack>

                <Box direction={{ xs: 'column', sm: 'row', width: 400, mx: 'auto' }} sx={{ mt: 3 }}>
                  <Grid container sx={{ p: 3 }} justifyContent="center" alignItems="center">
                    <ReceiptTransferList
                      customerPendingReceipts={customerPendingReceipts}
                      invoiceReceipts={receipts}
                      handleSelectedReceipts={handleSelectedReceipts}
                    />
                  </Grid>

                  {isRective ? (
                    <Card sx={{ p: 3 }}>
                      <Grid display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                        <Grid sx={{ width: 450 }}>
                          <AutocompleteInput
                            // reset={resetCompanyState}
                            label="Selector Ejercicio"
                            options={[...companiesList]}
                            onChange={handleFilterCompany}
                            defaultValue={companiesList[selectedIndex - 1]}
                          />
                        </Grid>
                        <Grid container sx={{ p: 3 }} justifyContent="center" alignItems="center">
                          <CustomList
                            title="Selecciona las facturas de las que se quiere realizar el abono"
                            items={
                              isEdit
                                ? invoicesForRective.filter((invoice) => selectedForRective.includes(invoice.id))
                                : invoicesForRective
                            }
                            checked={selectedForRective}
                            handleToggle={handleToggle}
                          />
                        </Grid>
                      </Grid>
                    </Card>
                  ) : null}
                </Box>

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                  <Button
                    color="primary"
                    variant="outlined"
                    component={RouterLink}
                    to={`${backPath}${serie}`}
                    startIcon={<ArrowBackIcon />}
                  >
                    Volver
                  </Button>
                  <Box>
                    <Label sx={{ height: 40, mr: 2 }} variant="outlined">
                      Factura Exenta
                      <Checkbox
                        checked={exempt}
                        onChange={(event) => {
                          setExempt(event.target.checked);
                        }}
                      />
                    </Label>
                    <Label sx={{ height: 40 }} variant="outlined">
                      Mostrar número de cuenta
                      <Checkbox
                        checked={showIBAN}
                        onChange={(event) => {
                          setShowIBAN(event.target.checked);
                        }}
                      />
                    </Label>
                    <LoadingButton
                      sx={{ ml: 2 }}
                      type="submit"
                      disabled={isEdit}
                      loading={isSubmitting}
                      loadingPosition="start"
                      startIcon={<SaveIcon />}
                      variant="contained"
                    >
                      {!isEdit ? 'Crear factura' : 'Guardar cambios'}
                    </LoadingButton>
                  </Box>
                </Box>
              </Stack>
            </Card>
          </Grid>
        </Grid>
      </Form>
    </FormikProvider>
  );
}
